import React, { FC, useEffect, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Switch, TimePicker } from 'antd';
import { FormWrapper } from 'common/components/form-wrapper/form-wrapper';
import { ReactComponent as SaveOutlined } from 'assets/icons/save.svg';
import { IModal } from 'common/models/model.interface';
import { observer } from 'mobx-react-lite';
import { TIME_h_mma } from 'common/constans/date.constants';
import { IAddress } from 'common/services/street-service/street.service.types';
import AddressSelect from 'features/eiq-manage/pages/eiq-manage-residents/components/address-select/address-select';
import trafficReportService from 'common/services/traffic-report-service/traffic-report.service';
import { TrafficLogReportType, trafficLogReportTypeOptions } from 'features/eiq-manage/enums/traffic-log-report-type.enum';
import { daysOfWeekOptions } from 'features/eiq-manage/enums/days-of-week.enum';
import { TrafficReportAddressType, trafficReportAddressTypeOptions } from 'features/eiq-manage/enums/traffic-report-address-type.enum';

const AddTrafficReportModal: FC<IModal<any, any>> = observer(({ title, isEdit, initData, isOpen, onClose }) => {
  const [form] = Form.useForm();
  const reportTypeValue = Form.useWatch('reportType', form);
  const addressTypeValue = Form.useWatch('addressType', form);

  const [selectedAddressId, setSelectedAddressId] = useState<number | null>(null);

  const handleAddressChanged = (value: IAddress | null) => {
    const addressId = value ? value.id : null;
    setSelectedAddressId(addressId);
  };

  useEffect(() => {
    if (form && isOpen) {
      form.setFieldsValue(initData);
    }
  }, [isOpen, form, initData]);

  const hideModal = (...rest: any) => {
    form.resetFields();
    if (onClose) {
      onClose(...rest);
    }
  };

  const onFinish = async (values: any) => {
    const body: { [key: string]: any } = {
      dayOfWeek: values.dayOfWeek,
      dayOfMonth: values.dayOfMonth,
      runAt: values.runAt ? values.runAt.format('HH:mm') : null,
      reportType: values.reportType,
      addressType: values.addressType,
      name: values.name,
      emails: values.emails,
    };

    if (values.addressType === TrafficReportAddressType.Include) {
      body.includedAddressIds = [values.address.addressId];
    } else {
      body.excludedAddressIds = [values.address.addressId];
    }

    await trafficReportService.create(body);

    hideModal(true);
  };

  return (
    <Modal centered title={title} width={550} open={isOpen} wrapClassName="custom-modal" closable={false}>
      <FormWrapper form={form} initialValues={initData} onFinish={onFinish} layout="vertical" validateTrigger="onSubmit">
        <Form.Item name="name" label="Report name" rules={[{ required: true, message: 'Report name is required!' }]}>
          <Input placeholder="Enter report name" />
        </Form.Item>
        <div className="edit-gate-form-list-wrapper">
          <h1 className="h1">Emails</h1>
          <Form.List
            name="emails"
            rules={[
              {
                validator: async (_, emails) => {
                  if (!emails || emails.length < 1) {
                    return Promise.reject(new Error('Email is required'));
                  }
                },
              },
            ]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div className="edit-gate-sub-list" key={key}>
                    <Form.Item
                      {...restField}
                      name={name}
                      label="Email"
                      rules={[
                        { required: true, message: 'Email is required' },
                        { type: 'email', message: 'Invalid email format' },
                      ]}>
                      <Input placeholder="Enter email" />
                    </Form.Item>

                    <Button htmlType="button" style={{ width: '100%' }} onClick={() => remove(name)} danger>
                      Remove
                    </Button>
                  </div>
                ))}

                <Button htmlType="button" onClick={() => add()} style={{ width: '100%' }}>
                  Add email
                </Button>
              </>
            )}
          </Form.List>
        </div>
        <Form.Item name="reportType" label="Report Type" rules={[{ required: true, message: 'Report type is required!' }]}>
          <Select placeholder="Select report type" options={trafficLogReportTypeOptions} allowClear />
        </Form.Item>
        {reportTypeValue && (
          <Form.Item name="runAt" label="Time" rules={[{ required: true, message: 'Time is required!' }]}>
            <TimePicker
              format={TIME_h_mma}
              placeholder="Select time"
              style={{ width: '100%' }}
              minuteStep={1}
              allowClear={false}
              use12Hours
            />
          </Form.Item>
        )}
        {reportTypeValue === TrafficLogReportType.Weekly && (
          <Form.Item name="dayOfWeek" label="Day of week" rules={[{ required: true, message: 'Day of week is required!' }]}>
            <Select placeholder="Select day of week" options={daysOfWeekOptions} allowClear />
          </Form.Item>
        )}
        {reportTypeValue === TrafficLogReportType.Monthly && (
          <Form.Item
            name="dayOfMonth"
            label="Day of month"
            rules={[
              { required: true, message: 'Day of month is required!' },
              { type: 'number', min: 1, max: 31, message: 'Should be from 1 to 31' },
            ]}>
            <InputNumber placeholder="Enter day of month" min={1} max={31} style={{ width: '100%' }} />
          </Form.Item>
        )}

        <Form.Item name="addressType" label="Address type" rules={[{ required: true, message: 'Address type is required!' }]}>
          <Select placeholder="Select address type" options={trafficReportAddressTypeOptions} allowClear />
        </Form.Item>

        {addressTypeValue && <AddressSelect value={selectedAddressId} setAddress={handleAddressChanged} isOpen={true} form={form} />}

        <Space className="footer">
          <Button type="link" onClick={() => hideModal(false)} className="eiq-button">
            Cancel
          </Button>
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />} className="eiq-button">
            Save
          </Button>
        </Space>
      </FormWrapper>
    </Modal>
  );
});

export default React.memo(AddTrafficReportModal, (prevProps, nextProps) => {
  return prevProps.isOpen === nextProps.isOpen;
});
